import React, { useEffect, useRef, useState } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import Heading from 'src/elements/typography/heading';
import Paragraph from 'src/elements/typography/paragraph';
import CloseIcon from 'src/images/CloseIcon';
import { palette } from 'src/styles/colors';
import {
  deleteLocalStorage,
  getLocalStorage,
  getSessionStorage,
  setLocalStorage,
  setSessionStorage,
} from 'src/functions/localStorageHandler';
import ButtonPrimary from 'src/elements/buttons/2023_Q4/ButtonPrimary';
import { IStaticContentfulImages } from 'src/types/contentful';
import { createHubSpotForm, prefillField, submitHubSpotFormBuilder } from 'src/functions/hubspot/hubspot.service';
import { usePopupController } from 'src/hooks/usePopupController';
import {
  EmailPopupHubspotForm,
  EmailPopupMainBackgroundOverlay,
  EmailPopupMainClose,
  EmailPopupMainContainer,
  EmailPopupMainForm,
  EmailPopupMainImage,
  EmailPopupMainLayout,
  EmailPopupMainText,
  EmailPopupMainThankYou,
  EmailPopupMainWrapper,
} from './ProEmailPopup.styles';
import { useFormResultController } from 'src/hooks/useFormResultController';

const submitHubSpotForm = submitHubSpotFormBuilder('#hubspotEmailPopupForm');

const ProEmailPopup = (): JSX.Element => {
  const { proModalAshleyFox } = useStaticQuery<IStaticContentfulImages>(graphql`
    {
      proModalAshleyFox: contentfulAsset(contentful_id: { eq: "226e11JoNIMOtlhM7ESw8" }) {
        gatsbyImageData(layout: CONSTRAINED, placeholder: BLURRED, width: 800)
      }
    }
  `);

  const [isPopupVisible, setIsPopupVisible] = useState(false);
  const [successVisible, setSuccessVisible] = useState(false);
  const [failureVisible, setFailureVisible] = useState(false);
  const [blockButton, setBlockButton] = useState(false);

  const pauseCompensationRef = useRef(0);
  const { isPaused } = usePopupController();
  const { isEmailSubscriptionSubmitted, setFormSubmission } = useFormResultController();

  const shownStorageId = 'proModalEmailPopupShown';
  const startedTimeStorageId = 'proModalEmailPopupStartedTime';
  const timeLeftStorageId = 'proModalEmailPopupTimeLeft';
  const showNextSessionId = 'proModalEmailPopupShowNextSession';

  const shouldShowPopup = (): boolean => {
    const popupShown = getLocalStorage(shownStorageId);
    const showNextSession = getLocalStorage(showNextSessionId);
    const sessionManagedShowNextSession = getSessionStorage(showNextSessionId); // this is used to control same session popup show

    if (isEmailSubscriptionSubmitted()) return false; // do not show popup if email is already submitted
    if (showNextSession === 'true' && !sessionManagedShowNextSession) return true; // show popup if it is set to show next session
    if (popupShown === 'true' || sessionManagedShowNextSession === 'true') {
      return false; // do not show popup if email is already submitted OR shown
    }

    return true;
  };

  const handleStorageOnModalOpen = (): void => {
    const popupShown = getLocalStorage(shownStorageId);
    const showNextSession = getLocalStorage(showNextSessionId);

    // This works in the following way:
    // The first time the popup is shown, it will set the showNextSession to true for both localStorage and sessionStorage
    // The sessionStorage controls the popup show in the same session, so the mouseleave event will not trigger the popup again
    // The localStorage controls the popup show in the next session
    // The next session that the popup is shown, it will set the showNextSession to false for localStorage, blocking the popup for all subsequent sessions
    // This method is needed since we trigger this function in all mouseleave events.
    if (!popupShown && !showNextSession) {
      setLocalStorage(showNextSessionId, 'true');
      setSessionStorage(showNextSessionId, 'true');
    } else if (showNextSession === 'true') {
      setLocalStorage(showNextSessionId, 'false');
      setLocalStorage(shownStorageId, 'true');
    }

    deleteLocalStorage(startedTimeStorageId);
    deleteLocalStorage(timeLeftStorageId);
  };

  const handleMouseLeave = (event: MouseEvent) => {
    if (!shouldShowPopup()) return;

    if (
      event.clientY <= 0 ||
      event.clientX <= 0 ||
      event.clientX >= window.innerWidth ||
      event.clientY >= window.innerHeight
    ) {
      handleOpen();
      handleStorageOnModalOpen();
    }
  };

  useEffect(() => {
    // set popup shown time to prevent showing it again
    if (isEmailSubscriptionSubmitted()) {
      setLocalStorage(shownStorageId, 'true');
      setLocalStorage(showNextSessionId, 'false');
      setSuccessVisible(true);
    }
  }, [isEmailSubscriptionSubmitted]);

  useEffect(() => {
    if (typeof document === 'undefined' || typeof window === 'undefined' || isPaused) return;

    document.addEventListener('mouseleave', handleMouseLeave);

    const formId = 'b5c092d9-163f-47b1-9fc3-7a5b6c953a64';
    createHubSpotForm({
      formContainer: '#hubspotEmailPopupForm',
      formId,
      onFormReadyCallback: () => {
        // Prefill form position field
        prefillField('hubspotEmailPopupForm', 'input[name="form_position"]', 'modal');
      },
      onFormSubmitCallback: () => {
        setBlockButton(true);
      },
      onFormSubmittedCallback: () => {
        setSuccessVisible(true);
        setFormSubmission();
      },
      errorCallback: () => {
        const failureTimeout = setTimeout(() => {
          setFailureVisible(true);
          clearTimeout(failureTimeout);
        }, 2000);
      },
    });

    return () => {
      document.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, [isPaused]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (!shouldShowPopup()) return;

    if (isPaused) {
      // Add 10s as compensation for the time the popup was paused
      pauseCompensationRef.current = 10000;
      return;
    }

    let timeLeft: string | number = getLocalStorage(timeLeftStorageId);
    let proModalEmailPopupStartedTime: string | number = getLocalStorage(startedTimeStorageId);
    const timeToShow = 1000 * 60;

    // First time setting up
    if (!timeLeft || !proModalEmailPopupStartedTime) {
      timeLeft = timeToShow;
      setLocalStorage(timeLeftStorageId, timeLeft.toString());

      proModalEmailPopupStartedTime = new Date().getTime();
      setLocalStorage(startedTimeStorageId, proModalEmailPopupStartedTime.toString());
    }

    // Includes compensation for the time the popup was paused
    const timeLeftForTimer = Math.max(parseInt(timeLeft as string), 0) + pauseCompensationRef.current;

    const timer = setTimeout(() => {
      // prevent showing popup if already shown by mouseleave
      if (!shouldShowPopup()) {
        return;
      }

      handleOpen();
      handleStorageOnModalOpen();
    }, timeLeftForTimer);

    return () => {
      // Prevent localStorage changes after render
      if (!shouldShowPopup()) {
        clearTimeout(timer);
        return;
      }

      const timeDifference = new Date().getTime() - parseInt(proModalEmailPopupStartedTime as string);

      setLocalStorage(timeLeftStorageId, (timeToShow - timeDifference).toString());
      clearTimeout(timer);
    };
  }, [isPopupVisible, isPaused]);

  const handleOpen = () => {
    setIsPopupVisible(true);
  };

  const handleClose = () => {
    setIsPopupVisible(false);
  };

  const title = '16 Lessons from Real Mighty Pro Hosts';
  const description =
    'Delivered right to your inbox—they’ll help you get concrete about the possibilities for your business';

  return (
    <EmailPopupMainWrapper
      className={isPopupVisible ? 'isVisible' : null}
      id='email-popup-main-wrapper'
    >
      <EmailPopupMainBackgroundOverlay onClick={handleClose} />
      <EmailPopupMainContainer>
        <EmailPopupMainClose
          onClick={() => {
            setIsPopupVisible(false);
          }}
        >
          <CloseIcon rectColor={palette.greyText} />
        </EmailPopupMainClose>
        <EmailPopupMainImage>
          <GatsbyImage
            image={proModalAshleyFox.gatsbyImageData}
            alt='Ashley Fox'
          />
        </EmailPopupMainImage>
        <EmailPopupMainLayout>
          <EmailPopupMainText className={successVisible || failureVisible ? 'hideMainText' : null}>
            <Heading
              text={title}
              weight='normal'
              standardSize='1.5'
              align='center'
              color={palette.greyText}
            />
            {description ? (
              <Paragraph
                weight='light'
                standardSize='1'
                align='center'
                color={palette.greyText}
              >
                {description}
              </Paragraph>
            ) : (
              <Paragraph
                weight='light'
                standardSize='1'
                align='center'
                color={palette.greyText}
              >
                Enter your email to get <b>9 SIMPLE STEPS to $1M</b> and a special bonus: a FREE copy of the
                best-selling book, <i>Purpose</i>
              </Paragraph>
            )}
            <EmailPopupMainForm>
              <EmailPopupHubspotForm id='hubspotEmailPopupForm' />
              <ButtonPrimary
                type='submit'
                text='Send Me the Secrets'
                disabled={blockButton ? true : false}
                colorScheme='Sunshine'
                onClick={submitHubSpotForm}
              />
              <Paragraph
                text={`You’re opting into educational emails from Mighty. \n \n You can adjust your preferences and unsubscribe at any time.`}
                size='0.625rem'
                align='center'
                weight='light'
                color={palette.greyText}
              />
            </EmailPopupMainForm>
          </EmailPopupMainText>

          <EmailPopupMainText className={successVisible ? 'isVisible' : 'hideMainText'}>
            <EmailPopupMainThankYou>
              <Heading
                standardSize='3.375'
                weight={700}
                align='center'
                text='You got it'
              />
              <Paragraph
                text='The first tips are on their way to your inbox'
                weight='light'
                size='1rem'
                color={palette.greyText}
                align='center'
              />
              <ButtonPrimary
                type='button'
                text='Return to Mighty Pro'
                onClick={handleClose}
                colorScheme='Sunshine'
              />
            </EmailPopupMainThankYou>
          </EmailPopupMainText>

          <EmailPopupMainText className={failureVisible ? 'isVisible' : 'hideMainText'}>
            <EmailPopupMainThankYou>
              <Heading
                text='Something went wrong!'
                weight='normal'
                standardSize='2.125'
                color={palette.greyText}
                align='center'
              />
              <Paragraph
                text='Please, try again later.'
                weight='light'
                size='1rem'
                color={palette.greyText}
                align='center'
              />
              <ButtonPrimary
                type='button'
                text='Return to home'
                onClick={handleClose}
                colorScheme='Sunshine'
              />
            </EmailPopupMainThankYou>
          </EmailPopupMainText>
        </EmailPopupMainLayout>
      </EmailPopupMainContainer>
    </EmailPopupMainWrapper>
  );
};

export default ProEmailPopup;
